import React, { useEffect, useMemo, useRef, useState } from 'react';
import { cx } from '@emotion/css';
import { HotColumn, HotTable } from '@handsontable/react';
import { registerAllModules } from 'handsontable/registry';
import { Chip } from '@mui/material';
import { getRenderer as getDefaultRenderer } from 'handsontable/renderers';
import { HANDSONTABLE_LICENSE } from '../../../licensing/constants';
import { ix } from '../../../utils/ix';
import { CellContextMenu } from './Spreadsheet.constants';
import { combineValidators, isChangeAllowed, isJsonString, normalizeChangedValue, prepareDataset, } from './Spreadsheet.utils';
import useStyles from './Spreadsheet.styles';
import { SpreadsheetInputSelectEditor, useSpreadsheetInputSelectRenderer, } from './components/SpreadsheetInputSelect';
import { ColumnHeaderCellRenderer } from './components/ColumnHeaderCell';
import 'handsontable/dist/handsontable.min.css';
import 'pikaday/css/pikaday.css';
registerAllModules();
export const Spreadsheet = React.forwardRef((props, ref) => {
    const { columns: propsColumns } = props;
    const selectRenderer = useSpreadsheetInputSelectRenderer();
    const columns = useMemo(() => propsColumns.map((col) => {
        const _id = Math.random().toString(36).substr(2, 9);
        return Object.assign(Object.assign({}, col), { _id });
    }), [propsColumns]);
    const [columnOrder, updateColumnOrder] = useState(columns.map((col) => col._id));
    const data = useMemo(() => {
        return prepareDataset(props.rowLimit ? props.data.slice(0, props.rowLimit) : props.data, columns);
    }, [props.data, props.rowLimit, columns]);
    const persistedValidation = useRef({});
    const editedRows = useRef(new Set());
    const classes = useStyles();
    useEffect(() => {
        updateColumnOrder(columns.map((col) => col._id));
    }, [columns]);
    const getRenderer = (type = 'text') => {
        if (type === 'select') {
            return selectRenderer;
        }
        return getDefaultRenderer(type);
    };
    const isEmptyValue = (value, definition) => {
        if (definition === null || definition === void 0 ? void 0 : definition.readOnly) {
            return true;
        }
        if ((definition === null || definition === void 0 ? void 0 : definition.type) === 'checkbox') {
            return value === definition.value;
        }
        return value === '' || value === null || typeof value === 'undefined';
    };
    const isDatasetRowEmpty = (row) => {
        const datasetRow = data[row];
        return Object.keys(datasetRow).every((key) => {
            const value = datasetRow[key];
            const definition = columns.find((col) => col.data === key);
            return isEmptyValue(value, definition);
        });
    };
    const revertChange = (change) => {
        /**
         * Currently beforeChange hook on handsontable allows accepting or rejecting the
         * whole change only, and this is not feasible when changing multiple values at
         * once (i.e. when dragging cell to other cells). In such cases, you may want to
         * accept changes per-cell (i.e. checkboxes wouldn't accept anything other than
         * booleans).
         *
         * This is a workaround that sets the next value to previous value if the onChange
         * callback returns boolean, making handsontable change the value to exactly the
         * same as it was, which looks like it hasn't changed. It works because the 'changes'
         * object is treated as immutable.
         */
        change[3] = change[2];
    };
    const onCellValueChange = (changes) => {
        (changes || []).forEach((change) => {
            var _a;
            if (!change)
                return;
            const [row, col, prev, next] = change;
            const columnDefinition = columns.find((c) => c.data === col);
            const isChangeValid = columnDefinition &&
                typeof row !== 'undefined' &&
                typeof col !== 'undefined';
            if (!isChangeValid)
                return;
            if (columnDefinition.readOnly) {
                revertChange(change);
                return;
            }
            if (!isEmptyValue(next, columnDefinition) || !isDatasetRowEmpty(row)) {
                editedRows.current.add(row);
            }
            const beforeChangeParsed = columnDefinition.type === 'select' && isJsonString(prev)
                ? JSON.parse(prev)
                : prev;
            const afterChangeParsed = columnDefinition.type === 'select' && isJsonString(next)
                ? JSON.parse(next)
                : next;
            const isOnChangeAllowed = (_a = props.onChange) === null || _a === void 0 ? void 0 : _a.call(props, row, col, beforeChangeParsed, afterChangeParsed);
            if (typeof isOnChangeAllowed === 'boolean' && !isOnChangeAllowed) {
                revertChange(change);
                return;
            }
            const normalizedChange = normalizeChangedValue(columnDefinition, afterChangeParsed);
            const allowed = isChangeAllowed(columnDefinition, normalizedChange);
            if (allowed) {
                change[3] = normalizedChange;
            }
            else {
                revertChange(change);
            }
        });
    };
    const beforeUpdateData = (sourceData) => {
        prepareDataset(sourceData, columns, true);
    };
    const removeErrorHighlightFromCell = (td) => {
        td.removeAttribute('aria-invalid');
        td.removeAttribute('errormessage');
    };
    const persistCellValidation = (row, column, isCellValid) => {
        if (isCellValid !== undefined) {
            persistedValidation.current = Object.assign(Object.assign({}, persistedValidation.current), { [`${row}-${column}`]: isCellValid });
        }
    };
    const addErrorHighLightToCell = (td, errorMessage) => {
        td.setAttribute('aria-invalid', 'true');
        td.setAttribute('aria-errormessage', errorMessage);
    };
    const validateCell = (column, cellValue) => {
        const columnDefinition = columns[column];
        return (columnDefinition.validators || []).find((v) => v(cellValue));
    };
    const handleCellValidation = (td, column, row, value) => {
        if (isDatasetRowEmpty(row)) {
            removeErrorHighlightFromCell(td);
            return;
        }
        const invalidValidator = validateCell(column, value);
        if (!invalidValidator)
            return;
        const errorMessage = invalidValidator(value);
        addErrorHighLightToCell(td, errorMessage);
    };
    const onAfterCellRenderer = (td, row, column, value, isCellValid) => {
        removeErrorHighlightFromCell(td);
        persistCellValidation(row, column, isCellValid);
        if (props.submitted) {
            if (props.validateOnlyEditedRows && !editedRows.current.has(row)) {
                return;
            }
            handleCellValidation(td, column, row, value);
        }
        else {
            if (persistedValidation.current[`${row}-${column}`] === false) {
                handleCellValidation(td, column, row, value);
            }
        }
    };
    const moveColumns = (movedColumnIndexes, finalIndex) => {
        let newColumnOrder = [...columnOrder];
        const movedCols = movedColumnIndexes.map((col) => newColumnOrder[col]);
        newColumnOrder = newColumnOrder.filter((col) => !movedCols.includes(col));
        newColumnOrder.splice(finalIndex, 0, ...movedCols);
        updateColumnOrder(newColumnOrder);
    };
    return (React.createElement(React.Fragment, null,
        React.createElement(Chip, { label: 'label', style: { display: 'none' } }),
        React.createElement(HotTable, { manualColumnResize: true, manualColumnMove: true, data: data, rowHeaders: true, colHeaders: true, contextMenu: CellContextMenu(props.rowLimit), rowHeights: 40, columnHeaderHeight: 48, beforeChange: onCellValueChange, afterChange: props.onChanged, afterCreateRow: props.onAfterCreateRow, beforeUpdateData: beforeUpdateData, ref: ref, id: ix(props.id, 'spreadsheet', 'container'), afterRenderer: (td, row, column, prop, value, cellProperties) => {
                onAfterCellRenderer(td, row, column, value, cellProperties.valid);
            }, viewportColumnRenderingOffset: Infinity, afterColumnMove: moveColumns, afterGetColumnHeaderRenderers: (renderers) => {
                renderers[0] = (index, container) => {
                    if (index === -1)
                        return;
                    const localId = columnOrder[index];
                    const definition = columns.find((col) => col._id === localId);
                    if (!definition)
                        return;
                    ColumnHeaderCellRenderer(definition)(container);
                };
            }, licenseKey: HANDSONTABLE_LICENSE, className: cx(classes.spreadsheet, 'htMiddle') }, columns.map((column) => (React.createElement(HotColumn, { key: `${column.data}`, data: column.data, title: column.title, width: column.width, multiple: column.multiple, source: column.source, options: column.options, filter: column.filter, readOnly: column.readOnly, trimDropdown: column.trimDropdown, visibleRows: column.visibleRows, renderer: getRenderer(column.type), dateFormat: column.dateFormat, className: cx('htMiddle', column.className), type: column.type === 'select' ? 'text' : column.type, validator: combineValidators(column.validators) }, column.type === 'select' ? (
        /* @ts-ignore Element inherits some props. It's hard to type it. */
        React.createElement(SpreadsheetInputSelectEditor, { "hot-editor": true, multiple: column.multiple, creatable: !column.forceSelect, columnName: `${column.data}`, renderOption: column.renderOption, virtualized: column.virtualized, virtualizationConfig: column.virtualizationConfig })) : null))))));
});
export default Spreadsheet;
//# sourceMappingURL=Spreadsheet.js.map